import React, { useMemo, useState } from 'react';
import { Alert, Button, Divider, Stack, Typography } from '@mui/material';
import { shortDate } from '../helpers';
import { YearbookRow, YearbookSheet } from '../types';

export interface DueReminder {
  sheet: YearbookSheet;
  row: YearbookRow;
}

const keyFor = (userId: string) => `nyhetslanseringar.yearbook.dismissedReminders.${userId}`;
const reminderKey = ({ row }: DueReminder) => `${row.id}:${row.sampleReminderDate}`;

const readDismissed = (userId: string): string[] => {
  try {
    const parsed = JSON.parse(localStorage.getItem(keyFor(userId)) || '[]') as unknown;
    return Array.isArray(parsed) ? parsed.filter((key): key is string => typeof key === 'string') : [];
  } catch {
    // A broken local value shows all reminders again.
    return [];
  }
};

const ReminderList: React.FC<{
  items: DueReminder[];
  sheetId: string;
  asOf: string;
  names?: Record<string, string>;
  onShow: (sheetId: string) => void;
}> = ({ items, sheetId, asOf, names, onShow }) => (
  <Stack
    direction="row"
    useFlexGap
    divider={<Divider orientation="vertical" flexItem />}
    sx={{ flexWrap: 'wrap', rowGap: 0.5, columnGap: 1.5 }}
  >
    {items.map(({ sheet, row }) => {
      const owner = names ? names[row.sampleReminderUserId || ''] : '';
      const parts = [sheet.name, row.articleName || row.articleNumber || 'Rad utan namn', shortDate(row.sampleReminderDate, asOf), owner].filter(Boolean);
      return (
        <Stack key={row.id} direction="row" spacing={0.5} alignItems="center">
          <Typography sx={{ fontSize: 14 }}>{parts.join(' · ')}</Typography>
          {sheet.id !== sheetId && <Button size="small" onClick={() => onShow(sheet.id)}>Visa</Button>}
        </Stack>
      );
    })}
  </Stack>
);

const ReminderAlerts: React.FC<{
  own: DueReminder[];
  others: DueReminder[];
  userId: string;
  sheetId: string;
  asOf: string;
  names: Record<string, string>;
  onShow: (sheetId: string) => void;
}> = ({ own, others, userId, sheetId, asOf, names, onShow }) => {
  const [dismissed, setDismissed] = useState<string[]>(() => readDismissed(userId));
  const shownOthers = useMemo(() => others.filter((item) => !dismissed.includes(reminderKey(item))), [dismissed, others]);

  const dismiss = () => {
    const open = new Set(others.map(reminderKey));
    const next = Array.from(new Set([...dismissed.filter((key) => open.has(key)), ...shownOthers.map(reminderKey)]));
    setDismissed(next);
    localStorage.setItem(keyFor(userId), JSON.stringify(next));
  };

  return (
    <>
      {own.length > 0 && (
        <Alert severity="error" icon={false} aria-label="Påminnelser om varuprover">
          <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
            {own.length === 1 ? 'Du har 1 påminnelse om varuprov' : `Du har ${own.length} påminnelser om varuprover`}
          </Typography>
          <ReminderList items={own} sheetId={sheetId} asOf={asOf} onShow={onShow} />
        </Alert>
      )}
      {shownOthers.length > 0 && (
        <Alert severity="error" icon={false} aria-label="Påminnelser från kollegor" onClose={dismiss} closeText="Dölj påminnelser från kollegor">
          <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
            {shownOthers.length === 1 ? '1 påminnelse om varuprov från en kollega' : `${shownOthers.length} påminnelser om varuprover från kollegor`}
          </Typography>
          <ReminderList items={shownOthers} sheetId={sheetId} asOf={asOf} names={names} onShow={onShow} />
        </Alert>
      )}
    </>
  );
};

export default ReminderAlerts;
